import type {
  CartItemSaleStatus,
  ICartChannelGroupV3,
  ICartDeliveryGroupV3,
  ICartProductOptionCategoryRequest,
  IUpdateCartItemRequestV2,
  OrderItemReferenceType,
  ProductType,
  SalePageDeliveryType,
} from '@/api/types';
import { getAdultContentMinAge } from '@/lib/adultContent';
import { request, requestV2, requestV3 } from '@/lib/request';
import { createQueryOption } from 'react-query-key-helper';

import { calcGroupShippingFee } from './shipping';

export interface ICartChannel {
  id: number;
  name: string;
  url: string;
  avatarUrl?: string;
}

// UI 표기를 단순화하기 위해 API 타입(OrderItemReferenceType)을 기반으로 구성
type CartItemReferenceType =
  | Extract<OrderItemReferenceType, 'PHYSICAL' | 'FUNDING' | 'BASIC_MEDIA'>
  | 'DIGITAL';

export interface ICartItem {
  id: string;
  productId: number;
  /** @deprecated v2에서는 options 사용 */
  optionId?: number;
  options?: ICartProductOptionCategoryRequest[];
  name: string;
  description?: string;
  optionDescription?: string;
  thumbnailUrl?: string;
  referenceType: CartItemReferenceType;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  shippingStartDate?: string;
  availability?: 'AVAILABLE' | 'SOLD_OUT' | 'ENDED' | 'SUSPENDED';
  // limits and flags
  minQuantity?: number;
  maxQuantity?: number;
  adjustedBySystem?: boolean;
  orderReferenceType?: OrderItemReferenceType;
  shippingFee?: number;
  selectable: boolean;
  // 코드 입력 관련
  hasVerificationCode?: boolean;
  adultOnlyMinAge?: number;
}

export interface ICartGroupSummary {
  productAmount: number;
  shippingFee: number;
  totalAmount: number;
}

export interface ICartGroup {
  groupId: string;
  channel: ICartChannel;
  items: ICartItem[];
  summary?: ICartGroupSummary;
  deliveryType?: 'CREATOR' | 'PLATFORM';
  isBundleDeliveryGroup?: boolean;
  bundleFreeShippingThresholdAmount?: number;
}

type ServerCartItem = ICartDeliveryGroupV3['items'][number];

const mapSaleStatusToAvailability = (
  saleStatus: CartItemSaleStatus
): ICartItem['availability'] => {
  switch (saleStatus) {
    case 'ON_SALE':
      return 'AVAILABLE';
    case 'SOLD_OUT':
      return 'SOLD_OUT';
    case 'ENDED':
      return 'ENDED';
    case 'STOPPED':
      return 'SUSPENDED';
    default:
      return 'AVAILABLE';
  }
};

const mapProductTypeToReferenceType = (
  productType: ProductType
): CartItemReferenceType => {
  switch (productType) {
    case 'PHYSICAL':
      return 'PHYSICAL';
    case 'FUNDING':
      return 'FUNDING';
    case 'BASIC_MEDIA':
      return 'BASIC_MEDIA';
    default:
      return 'DIGITAL';
  }
};

const mapProductTypeToOrderReferenceType = (
  productType: ProductType
): OrderItemReferenceType => {
  switch (productType) {
    case 'PHYSICAL':
      return 'PHYSICAL';
    case 'BASIC_MEDIA':
      return 'BASIC_MEDIA';
    case 'RANDOM_MEDIA':
      return 'RANDOM_MEDIA';
    case 'CUSTOM_MEDIA':
      return 'CUSTOM_MEDIA';
    case 'GACHA_TICKET':
      return 'GACHA_TICKET';
    case 'FUNDING':
      return 'FUNDING';
    case 'CLASS':
      return 'CLASS';
    default:
      return 'BASIC_MEDIA';
  }
};

export const cartQueryOptions = {
  groups: createQueryOption<
    ICartGroup[],
    undefined,
    { deliveryAddressId?: number } | undefined
  >({
    apiUrl: pathVariables => {
      const deliveryAddressId = pathVariables?.deliveryAddressId;
      const hasAddressParam =
        deliveryAddressId !== undefined && deliveryAddressId !== null;
      return `/users/cart${hasAddressParam ? `?deliveryAddressId=${deliveryAddressId}` : ''}`;
    },
    queryFn: async (apiUrl: string) => {
      const channelGroups = await requestV3.get<ICartChannelGroupV3[]>(apiUrl);

      const groups: ICartGroup[] = channelGroups.flatMap(channelGroup =>
        channelGroup.deliveryGroups.map((deliveryGroup, idx) => {
          const items: ICartItem[] = deliveryGroup.items.map(serverItemRaw => {
            const serverItem = serverItemRaw as ServerCartItem;
            const salePageId = serverItem.salePageId;
            const baseUnitPrice = serverItem.product.price?.amount ?? 0;
            const optionExtra =
              serverItem.options?.reduce(
                (sum, optionCategory) =>
                  sum +
                  optionCategory.options.reduce(
                    (catSum, opt) => catSum + (opt.price?.amount ?? 0),
                    0
                  ),
                0
              ) ?? 0;
            const unitPrice = baseUnitPrice + optionExtra;
            const quantity = serverItem.quantity;
            const totalPrice = unitPrice * quantity;
            const availableStock = serverItem.availableStock;

            const referenceType = serverItem.product?.type
              ? mapProductTypeToReferenceType(
                  serverItem.product.type as ProductType
                )
              : 'DIGITAL';

            const thumbnailUrl = serverItem?.thumbnailUrl;
            const itemShippingFee = (() => {
              const feeRaw = serverItem.deliveryFee;
              const amount = feeRaw?.money?.amount;
              return typeof amount === 'number' ? amount : undefined;
            })();
            const options: ICartProductOptionCategoryRequest[] | undefined =
              serverItem.options?.map(optionCategory => ({
                id: optionCategory.id,
                optionIds: optionCategory.options.map(opt => opt.id),
              }));
            const firstOptionId = serverItem.options?.[0]?.options?.[0]?.id;

            const optionCategories = serverItem.options ?? [];

            const optionSummary = optionCategories
              .map(optionCategory => {
                const hasNoSelection = optionCategory.options.some(
                  opt => opt.id === -1
                );
                if (hasNoSelection) {
                  return `[옵션-${optionCategory.name}]: 선택안함`;
                }

                const optionNames = optionCategory.options
                  .map(opt => opt.name)
                  .join(', ');

                return `[옵션-${optionCategory.name}]: ${optionNames}`;
              })
              .filter(Boolean)
              .join(' / ');
            const optionDescription = optionSummary || undefined;

            const baseDescription = (
              serverItem?.description ?? serverItem.product.description
            )?.trim();
            const productName = serverItem.product.name?.trim();
            const description =
              baseDescription && baseDescription !== productName
                ? baseDescription
                : undefined;

            return {
              id: String(serverItem.id),
              productId: serverItem.product.id,
              optionId: firstOptionId,
              options,
              name: serverItem.product.name,
              description,
              optionDescription,
              thumbnailUrl,
              referenceType,
              quantity,
              unitPrice,
              totalPrice,
              salePageId,
              shippingStartDate: serverItem.shippingStartDate,
              availability: mapSaleStatusToAvailability(serverItem.saleStatus),
              minQuantity: 1,
              maxQuantity: availableStock,
              adjustedBySystem: serverItem.isModifiedBySystem,
              orderReferenceType: mapProductTypeToOrderReferenceType(
                serverItem.product.type
              ),
              shippingFee: itemShippingFee,
              selectable: serverItem.selectable,
              hasVerificationCode: serverItem.requiresPromotionCode,
              adultOnlyMinAge: getAdultContentMinAge(serverItem),
            };
          });

          const productAmount = items.reduce(
            (acc, it) => acc + it.totalPrice,
            0
          );

          const saleBundleId = deliveryGroup.saleBundle?.saleBundleId;
          const bundleFreeShippingThresholdAmount =
            typeof deliveryGroup.saleBundle?.freeShippingThreshold?.amount ===
            'number'
              ? deliveryGroup.saleBundle.freeShippingThreshold.amount
              : undefined;

          const isBundleDeliveryGroup = deliveryGroup.type === 'BUNDLE';
          const shippingFee = calcGroupShippingFee(items, productAmount, {
            isBundle: isBundleDeliveryGroup,
            bundleFreeThreshold: bundleFreeShippingThresholdAmount,
          });
          const totalAmount = productAmount + shippingFee;
          const salePageId = deliveryGroup.salePageId;
          const firstItemId = deliveryGroup.items?.[0]?.id;
          const safeGroupId =
            saleBundleId !== undefined && saleBundleId !== null
              ? `bundle:${saleBundleId}:${channelGroup.channel.id}:${firstItemId ?? idx}`
              : salePageId !== undefined && salePageId !== null
                ? `sale-page:${salePageId}:${channelGroup.channel.id}:${firstItemId ?? idx}`
                : `${deliveryGroup.type}:${channelGroup.channel.id}:${firstItemId ?? idx}`;
          return {
            groupId: safeGroupId,
            channel: {
              id: channelGroup.channel.id,
              name: channelGroup.channel.name,
              url: channelGroup.channel.url,
              avatarUrl: channelGroup.channel.thumbnailUrl,
            },
            items,
            summary: {
              productAmount,
              shippingFee,
              totalAmount,
            },
            deliveryType: deliveryGroup.deliveryType as
              | SalePageDeliveryType
              | undefined,
            isBundleDeliveryGroup,
            bundleFreeShippingThresholdAmount,
          } satisfies ICartGroup;
        })
      );

      return groups;
    },
  }),
  addItems: createQueryOption({
    apiUrl: () => '/users/cart',
    queryFn: (apiUrl: string, payload: IUpdateCartItemRequestV2[]) =>
      requestV2.post<boolean>(apiUrl, payload),
  }),
  updateItems: createQueryOption({
    apiUrl: () => '/users/cart',
    queryFn: (apiUrl: string, payload: IUpdateCartItemRequestV2[]) =>
      requestV2.post<boolean>(apiUrl, payload),
  }),
  deleteItems: createQueryOption({
    apiUrl: (cartIds: number[]) => `/users/cart/${cartIds.join(',')}`,
    queryFn: (apiUrl: string) => request.delete<boolean>(apiUrl),
  }),
  count: createQueryOption({
    apiUrl: () => '/users/cart/count',
    queryFn: (apiUrl: string) => request.get<number>(apiUrl),
  }),
};
